import React, { HTMLAttributes } from "react";
import { Input } from "@trussle/external-components";
import cn from "classnames";
import Joi from "joi";
import { PurposeInput } from "../common";
import { useFormContext } from "../context/FormContext";
import { CTA } from "./CTA";
import { ErrorMessage } from "./ErrorMessage";
import { HomeBuyingStage, NextSteps } from "../../../../types/common";

const schema = Joi.object({
  mortgagePurpose: Joi.string().required().messages({
    "string.empty": "mortgage purpose"
  }),
  propertyValue: Joi.string().required().messages({
    "string.empty": "property value"
  }),
  borrowAmount: Joi.string().required().messages({
    "string.empty": "borrowing amount"
  }),
  income: Joi.string().required().messages({
    "string.empty": "combined income"
  }),
  homeBuyingStage: Joi.string().required().messages({
    "string.empty": "home buying stage"
  }),
  deposit: Joi.string().required().messages({
    "string.empty": "deposit"
  }),
  nextSteps: Joi.string().required().messages({
    "string.empty": "next steps"
  })
});

type IPurchaseFormProps = HTMLAttributes<HTMLFormElement>;

export const PurchaseForm: React.FC<IPurchaseFormProps> = ({ className, ...rest }) => {
  const { formData, setFormData, recalculateStep } = useFormContext();
  const [errors, setErrors] = React.useState<Joi.ValidationError | null>(null);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const inputs = new FormData(e.currentTarget);
    const inputEntries = Object.fromEntries(inputs.entries());

    const { error } = schema.validate(inputEntries, { abortEarly: false });

    if (error) {
      setErrors(error);
    } else {
      setErrors(null);

      recalculateStep();
    }
  };

  return (
    <form {...rest} className={cn("heroff2-form", className)} onSubmit={onSubmit}>
      <h2 className="heroff2-form-heading-purchase">Join thousands of homeowners with mortgage advice you can trust.</h2>
      <PurposeInput key="purchaseform-purposeinput" />
      <Input
        name="borrowAmount"
        variant="currency"
        label="How much do you want to borrow?"
        value={formData.borrowAmount}
        onChange={(e) => {
          setFormData((prev) => ({
            ...prev,
            borrowAmount: e.target.value
          }));
        }}
      />
      <Input
        variant="currency"
        label="What is your combined income?"
        name="income"
        value={formData.combinedIncome}
        onChange={(e) => {
          setFormData((prev) => ({
            ...prev,
            combinedIncome: e.target.value
          }));
        }}
      />
      <Input
        name="homeBuyingStage"
        variant="select"
        label="What stage are you at?"
        value={formData.homeBuyingStage}
        onChange={(e) => {
          setFormData((prev) => ({
            ...prev,
            homeBuyingStage: e.target.value as HomeBuyingStage
          }));
        }}
        options={[
          { label: "", value: "", hidden: true },
          { label: "I've had an offer accepted", value: HomeBuyingStage.Accepted },
          { label: "I'm viewing properties", value: HomeBuyingStage.Viewing },
          { label: "I'm just researching", value: HomeBuyingStage.Researching }
        ]}
      />
      <Input
        label="How much is your deposit?"
        variant="currency"
        name="deposit"
        value={formData.deposit}
        onChange={(e) => {
          setFormData((prev) => ({
            ...prev,
            deposit: e.target.value
          }));
        }}
      />
      <Input
        name="nextSteps"
        variant="select"
        label="What are you looking to do now?"
        options={[
          { label: "", value: "", hidden: true },
          { label: "Apply for a mortgage", value: NextSteps.Apply },
          { label: "Compare the latest mortgage rates", value: NextSteps.Compare },
          { label: "See how much I can borrow", value: NextSteps.MIP }
        ]}
        value={formData.nextSteps}
        onChange={(e) => {
          setFormData((prev) => ({
            ...prev,
            nextSteps: e.target.value as NextSteps
          }));
        }}
      />
      <ErrorMessage errors={errors} />
      <CTA>Get Started</CTA>
    </form>
  );
};
